import {
  List,
  Datagrid,
  FunctionField,
  TextInput,
  SelectInput,
  BooleanInput,
  RaRecord,
} from 'react-admin';
import { DateTimeField } from '../shared/DateFields';
import { useCompanies, companyNameByViaId } from '../shared/companies';
import { Chip, Stack, Tooltip } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { classificationLabel } from '../shared/status';
import {
  ClampedText,
  CompanyChip,
  compactDatagridSx,
} from '../shared/listCells';
import { useFeature } from '../admin/featureFlags/FeatureGate';
import { FEATURE_EMAIL_DETAILS, participantsOf, recipientsSummary } from './emailStructure';

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'success' | 'info';

// Kolory etykiet klasyfikacji (W2) — spójne z panelem driver-documents w FF.
export const CLASSIFICATION_COLORS: Record<string, ChipColor> = {
  ORDER: 'success',
  INQUIRY: 'info',
  CHANGE: 'warning',
  COMPLAINT: 'error',
  INTERNAL: 'default',
  OTHER: 'default',
};
/**
 * Nazwa spółki z danych — numery spółek nie mogą siedzieć w kodzie.
 * Na liście jako znacznik z krajem; pełna nazwa w podpowiedzi.
 */
const Company = ({
  viaCompanyId,
  fallback,
  fallbackCountry,
}: {
  viaCompanyId?: string | null;
  fallback?: string | null;
  fallbackCountry?: string | null;
}) => {
  const companies = useCompanies();
  const known = companies.find((s) => s.viaCompanyId === viaCompanyId);
  return (
    <CompanyChip
      name={companyNameByViaId(companies, viaCompanyId) || fallback}
      country={known?.country ?? fallbackCountry}
    />
  );
};

export const ClassificationChip = ({ record }: { record: RaRecord }) => {
  const c = record.classification as string | null;
  if (!c) return <span>—</span>;
  const conf = record.classificationConfidence as number | null;
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
      <Chip size="small" variant="outlined" color={CLASSIFICATION_COLORS[c] ?? 'default'} label={classificationLabel(c)} />
      {conf != null && (
        <Chip
          size="small"
          color={conf >= 0.8 ? 'success' : conf >= 0.6 ? 'warning' : 'error'}
          label={`${Math.round(conf * 100)}%`}
        />
      )}
    </Stack>
  );
};

const classChoices = Object.keys(CLASSIFICATION_COLORS).map((id) => ({ id, name: classificationLabel(id) }));

const filters = [
  <TextInput source="q" label="Szukaj" alwaysOn />,
  <SelectInput source="classification" label="Klasyfikacja" choices={classChoices} />,
  // `defaultValue`: filtr dodany z „Dodaj filtr” od razu działa (e2e, 28.09.2026).
  <BooleanInput source="hasAttachments" label="Z załącznikami" defaultValue />,
];

/**
 * Filtry po nadawcy i odbiorcy (03.11.2026, `emailDetails`, S-03.11-02) —
 * z tabeli uczestników maila, z zapasem na stare kolumny po stronie API.
 * Klucze `sender` / `recipient` to dane (react-admin je zapamiętuje).
 */
const participantFilters = [
  <TextInput source="sender" label="Nadawca" data-testid="email-filter-sender" />,
  <TextInput source="recipient" label="Odbiorca (Do, DW)" data-testid="email-filter-recipient" />,
];

/** Kolumna „Do/DW”: pierwszy odbiorca i „+n”, pełna lista w podpowiedzi. */
export const RecipientsCell = ({ record }: { record: RaRecord }) => {
  const { short, full } = recipientsSummary(participantsOf(record));
  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{full}</span>} disableHoverListener={!full}>
      <span data-testid="email-recipients-cell">
        <ClampedText text={short} maxWidth={{ xs: 160, xl: 260 }} />
      </span>
    </Tooltip>
  );
};

// Skrzynka — lista maili (wzorowana na driver-documents z FF).
export const EmailList = () => {
  const detailsOn = useFeature(FEATURE_EMAIL_DETAILS);
  return (
  <List filters={detailsOn ? [...filters, ...participantFilters] : filters} sort={{ field: 'receivedAt', order: 'DESC' }} perPage={25}>
    {/* Wiersz w jednej linii od 1366 px (28.09.2026): nadawca i temat
        skracane z podpowiedzią, czas bez sekund, spółka jako znacznik. */}
    <Datagrid rowClick="show" bulkActionButtons={false} sx={compactDatagridSx}>
      <DateTimeField source="receivedAt" label="Otrzymano" />
      <FunctionField
        label="Od"
        render={(r: RaRecord) => (
          <ClampedText
            text={`${r.fromName ? r.fromName + ' ' : ''}<${r.fromAddress ?? '?'}>`}
            maxWidth={{ xs: 220, xl: 360 }}
          />
        )}
      />
      {detailsOn && <FunctionField label="Do/DW" render={(r: RaRecord) => <RecipientsCell record={r} />} />}
      <FunctionField
        label="Temat"
        render={(r: RaRecord) => <ClampedText text={r.subject} maxWidth={{ xs: 280, xl: 520 }} />}
      />
      <FunctionField label="Klasyfikacja" render={(r: RaRecord) => <ClassificationChip record={r} />} />
      <FunctionField
        label="Zał."
        render={(r: RaRecord) =>
          r.hasAttachments ? (
            <Tooltip title={`${(r.attachments as unknown[])?.length ?? ''} załącznik(ów)`}>
              <AttachFileIcon fontSize="small" color="action" />
            </Tooltip>
          ) : null
        }
      />
      <FunctionField
        label="Spółka"
        render={(r: RaRecord) => (
          <Company
            viaCompanyId={r.viaCompanyId as string | undefined}
            fallback={(r.company as RaRecord | undefined)?.name as string | undefined}
            fallbackCountry={(r.company as RaRecord | undefined)?.country as string | undefined}
          />
        )}
      />
    </Datagrid>
  </List>
  );
};
